(() => {
  'use strict';
  const initialise = section => {
    if (section.dataset.productReady) return;
    section.dataset.productReady = 'true';
    const messages = JSON.parse(section.querySelector('[data-product-messages]')?.textContent || '{}');
    const variants = JSON.parse(section.querySelector('[data-product-variants]')?.textContent || '[]');
    let request;
    let pendingUrl;
    let focusTarget;

    // Valeurs actuellement choisies, option par option (dans l'ordre des options).
    const groups = () => [...section.querySelectorAll('[data-option-position]')].sort((a, b) => a.dataset.optionPosition - b.dataset.optionPosition);
    const currentState = () => JSON.parse(section.querySelector('[data-product-state]')?.textContent || '{}');
    const currentValues = () => {
      const variant = variants.find(v => v.id === currentState().variantId);
      if (variant) return variant.options.slice();
      return groups().map(g => g.dataset.optionSingle ?? g.querySelector('[aria-pressed="true"]')?.dataset.optionValue ?? null);
    };
    // Une valeur est proposée si une variante en stock existe avec les choix des options précédentes.
    const matches = (variant, values, index, value) => variant.available && variant.options[index] === value && values.slice(0, index).every((v, i) => v === null || variant.options[i] === v);
    const refreshPills = () => {
      const values = currentValues();
      let anyBlocked = false;
      groups().forEach((group, index) => {
        if (!group.matches('[data-option-group]')) return;
        group.querySelectorAll('[data-option-value]').forEach(button => {
          const value = button.dataset.optionValue;
          const possible = variants.some(v => matches(v, values, index, value));
          button.disabled = !possible;
          button.classList.toggle('is-unavailable', !possible);
          button.setAttribute('aria-pressed', String(value === values[index]));
          button.title = possible ? '' : 'Indisponible avec votre sélection';
        });
        const label = group.querySelector('[data-option-selected]');
        anyBlocked = anyBlocked || !!group.querySelector('.option-pill:disabled');
        if (label && values[index] != null) label.textContent = values[index];
      });
      const hint = section.querySelector('.product-option__hint');
      if (hint) hint.hidden = !anyBlocked;
    };
    // Choisit la variante en stock la plus proche de la sélection actuelle.
    const pickVariant = (index, value) => {
      const values = currentValues();
      const candidates = variants.filter(v => matches(v, values, index, value));
      if (!candidates.length) return null;
      const score = v => v.options.reduce((total, option, i) => total + (i > index && option === values[i] ? 1 : 0), 0);
      return candidates.sort((a, b) => score(b) - score(a))[0];
    };

    const enhance = () => {
      section.querySelectorAll('[data-product-enhanced]').forEach(el => { el.hidden = false; });
      const input = section.querySelector('[data-product-variant-id]');
      if (input) input.disabled = false;
      refreshPills();
    };
    const showImage = button => {
      if (!button) return;
      const image = section.querySelector('[data-product-main-image]');
      if (!image) return;
      image.src = button.dataset.imageUrl;
      if (button.dataset.imageSrcset) image.srcset = button.dataset.imageSrcset;
      else image.removeAttribute('srcset');
      image.alt = button.dataset.imageAlt || '';
      section.querySelectorAll('[data-product-thumbnail]').forEach(el => el.setAttribute('aria-pressed', String(el === button)));
    };
    const loadSelection = async url => {
      request?.abort();
      request = new AbortController();
      const activeRequest = request;
      pendingUrl = url;
      const form = section.querySelector('.product-form');
      const status = section.querySelector('[data-product-status]');
      const error = section.querySelector('[data-product-load-error]');
      const previousQuantity = Number(form.querySelector('[name="quantity"]')?.value || 1);
      form.setAttribute('aria-busy', 'true');
      form.querySelector('[data-add-to-cart]').disabled = true;
      form.querySelector('[data-product-variant-id]').disabled = true;
      error.hidden = true;
      status.textContent = messages.loading || '';
      try {
        const response = await fetch(url, {signal: activeRequest.signal, headers: {'Accept': 'text/html'}});
        if (!response.ok) throw new Error('Product unavailable');
        const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
        const next = documentFragment.querySelector('[data-product-selection]');
        if (!next) throw new Error('Product unavailable');
        if (activeRequest !== request) return;
        const state = JSON.parse(next.querySelector('[data-product-state]').textContent);
        const quantity = next.querySelector('[name="quantity"]');
        const min = Number(quantity.min || 1);
        const step = Number(quantity.step || 1);
        const max = quantity.max ? Number(quantity.max) : Infinity;
        if (previousQuantity >= min && previousQuantity <= max && (previousQuantity - min) % step === 0) quantity.value = previousQuantity;
        section.querySelector('[data-product-selection]').replaceWith(next);
        const specs = documentFragment.querySelector('[data-product-specs]');
        if (specs) section.querySelector('[data-product-specs]')?.replaceWith(specs);
        enhance();
        if (state.imageId) showImage([...section.querySelectorAll('[data-product-thumbnail]')].find(el => el.dataset.imageId === String(state.imageId)));
        const address = new URL(location.href);
        address.searchParams.delete('section_id');
        address.searchParams.delete('option_values');
        if (state.variantId) address.searchParams.set('variant', state.variantId);
        history.replaceState({}, '', address);
        if (focusTarget) section.querySelector(`[data-option-position="${focusTarget.position}"] [data-option-value="${CSS.escape(focusTarget.value)}"]`)?.focus({preventScroll: true});
        status.textContent = messages.updated || '';
        pendingUrl = null;
      } catch (failure) {
        if (failure.name === 'AbortError' || activeRequest !== request) return;
        status.textContent = '';
        error.hidden = false;
      } finally {
        if (activeRequest === request) section.querySelector('.product-form')?.removeAttribute('aria-busy');
      }
    };
    section.addEventListener('click', event => {
      const pill = event.target.closest('[data-option-value]');
      if (pill && !pill.disabled) {
        const group = pill.closest('[data-option-position]');
        const index = groups().indexOf(group);
        const variant = pickVariant(index, pill.dataset.optionValue);
        if (!variant || variant.id === currentState().variantId) return;
        group.querySelectorAll('[data-option-value]').forEach(el => el.setAttribute('aria-pressed', String(el === pill)));
        focusTarget = {position: group.dataset.optionPosition, value: pill.dataset.optionValue};
        const url = new URL(section.dataset.productUrl, location.origin);
        const preview = new URL(location.href).searchParams.get('preview_theme_id');
        if (preview) url.searchParams.set('preview_theme_id', preview);
        url.searchParams.set('variant', variant.id);
        url.searchParams.set('section_id', section.dataset.sectionId);
        loadSelection(url);
        return;
      }
      const image = event.target.closest('[data-product-thumbnail]');
      if (image) showImage(image);
      if (event.target.closest('[data-product-retry]') && pendingUrl) loadSelection(pendingUrl);
    });
    enhance();
  };
  document.querySelectorAll('[data-product-detail]').forEach(initialise);
  document.addEventListener('shopify:section:load', event => event.target.querySelectorAll('[data-product-detail]').forEach(initialise));
})();
